import React, { useEffect, createElement } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AnnouncementBar from './components/AnnouncementBar';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ImpactMetrics from './components/ImpactMetrics';
import WhyItWorks from './components/WhyItWorks';
import Features from './components/Features';
import Interfaces from './components/Interfaces';
import Integrations from './components/Integrations';
import PoweredByFluidWeb from './components/PoweredByFluidWeb';
import AdvancedCapabilities from './components/AdvancedCapabilities';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import CTA from './components/CTA';
import Footer from './components/Footer';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FluidFormPreview from './pages/FluidFormPreview';
import TryFluidForm from './pages/TryFluidForm';
import ChatBubble from './components/ChatBubble';
import VerticalB from './pages/VerticalB';
export function App() {
  useEffect(() => {
    // Preconnect to external domains for faster resource loading
    const preconnectDomains = ['https://uploadthingy.s3.us-west-1.amazonaws.com', 'https://cdn.magicpatterns.com', 'https://app.fluid-form.ai', 'https://accounts.fluid-form.ai', 'https://www.googletagmanager.com'];
    preconnectDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
    // Google Analytics (gtag.js)
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-9F433GY2HD';
      document.head.appendChild(gtagScript);
      const gtagInit = document.createElement('script');
      gtagInit.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-9F433GY2HD');
      `;
      document.head.appendChild(gtagInit);
    }
    // ── Default SEO metadata (home page) ──
    function setMeta(attr: string, value: string, content: string) {
      let el = document.querySelector(`meta[${attr}="${value}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    }
    document.title = 'FluidForm — AI-Native Forms That Feel Human';
    setMeta('name', 'description', 'Let users respond naturally with text, voice, documents, and photos — and receive clean, validated, schema-aligned data every time. Build forms in minutes, not days.');
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://fluid-form.ai';
    // Open Graph
    setMeta('property', 'og:title', 'FluidForm — AI-Native Forms That Feel Human');
    setMeta('property', 'og:description', 'Let users respond naturally with text, voice, documents, and photos — and receive clean, validated, schema-aligned data every time.');
    setMeta('property', 'og:url', 'https://fluid-form.ai');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'FluidForm');
    setMeta('property', 'og:image', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2KuE4GiXagr4eVmupSGvce/fluid-form-logo.png');
    setMeta('property', 'og:image:alt', 'FluidForm — AI-native forms that feel human');
    // Twitter Card
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', 'FluidForm — AI-Native Forms That Feel Human');
    setMeta('name', 'twitter:description', 'Let users respond naturally with text, voice, documents, and photos — and receive clean, validated, schema-aligned data every time.');
    setMeta('name', 'twitter:image', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2KuE4GiXagr4eVmupSGvce/fluid-form-logo.png');
    setMeta('name', 'twitter:site', '@fluidform');
    // Robots
    setMeta('name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large');
    // JSON-LD Structured Data for home page
    const homeJsonLd = {
      '@context': 'https://schema.org',
      '@graph': [{
        '@type': 'WebSite',
        '@id': 'https://fluid-form.ai/#website',
        url: 'https://fluid-form.ai',
        name: 'FluidForm',
        description: 'AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.'
      }, {
        '@type': 'WebPage',
        '@id': 'https://fluid-form.ai/#webpage',
        url: 'https://fluid-form.ai',
        name: 'FluidForm — AI-Native Forms That Feel Human',
        description: 'Let users respond naturally with text, voice, documents, and photos — and receive clean, validated, schema-aligned data every time.',
        isPartOf: {
          '@id': 'https://fluid-form.ai/#website'
        }
      }, {
        '@type': 'SoftwareApplication',
        name: 'FluidForm',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://fluid-form.ai',
        description: 'AI-native form engine that captures structured data from text, voice, documents, and photos with schema-enforced validation.',
        offers: [{
          '@type': 'Offer',
          name: 'Free Plan',
          price: '0',
          priceCurrency: 'USD',
          description: '1 user, 3 forms, 25 submissions/month, AI form builder'
        }, {
          '@type': 'Offer',
          name: 'Pro Plan',
          price: '39',
          priceCurrency: 'USD',
          description: '3 users, 10 forms, 1,000 submissions/month, advanced AI validations'
        }]
      }, {
        '@type': 'FAQPage',
        mainEntity: [{
          '@type': 'Question',
          name: 'What is FluidForm?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FluidForm is an AI-native form engine that captures structured data from natural inputs — text, voice, documents, and photos — and outputs schema-aligned submissions.'
          }
        }, {
          '@type': 'Question',
          name: 'How is this different from a chatbot?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Chat UIs can talk, but they're unreliable at producing complete, schema-valid submissions. FluidForm is schema-first: conversation is used to complete and validate a form — then you get clean structured output."
          }
        }, {
          '@type': 'Question',
          name: 'How reliable is the AI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FluidForm is designed for reliability through schema constraints, validation, and confirmations. You control when AI can auto-fill vs. when it must ask or confirm.'
          }
        }, {
          '@type': 'Question',
          name: 'Can I customize the look and feel?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Use styling controls or custom CSS. Keep full branding and UX control.'
          }
        }, {
          '@type': 'Question',
          name: 'Is FluidForm suitable for regulated industries like fintech, insurance, and real estate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes. FluidForm is built schema-first — every submission is validated, structured, and audit-ready. It's designed for workflows where data accuracy and compliance matter."
          }
        }, {
          '@type': 'Question',
          name: 'How does FluidForm handle sensitive documents like IDs and financial statements?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'FluidForm extracts and validates document data without storing sensitive files beyond what your configuration allows. You control what gets captured, validated, and passed to your systems.'
          }
        }, {
          '@type': 'Question',
          name: 'Can FluidForm integrate with our existing onboarding or CRM tools?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. FluidForm outputs clean, schema-aligned JSON that connects to your existing stack. Custom integrations are available on Pro and Enterprise plans.'
          }
        }]
      }, {
        '@type': 'Organization',
        '@id': 'https://fluid-form.ai/#organization',
        name: 'FluidForm, Inc.',
        url: 'https://fluid-form.ai',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@fluid-form.ai',
          contactType: 'customer support'
        }
      }]
    };
    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'home-jsonld';
    ldScript.textContent = JSON.stringify(homeJsonLd);
    document.head.appendChild(ldScript);
  }, []);
  return <BrowserRouter data-id="element-0">
      <Routes data-id="element-1">
        <Route path="/terms" element={<TermsOfUse data-id="element-3" />} data-id="element-2" />
        <Route path="/privacy" element={<PrivacyPolicy data-id="element-5" />} data-id="element-4" />
        <Route path="/preview" element={<FluidFormPreview data-id="element-7" />} data-id="element-6" />
        <Route path="/try" element={<TryFluidForm data-id="element-9" />} data-id="element-8" />
        <Route path="/b" element={<VerticalB data-id="element-11" />} data-id="element-10" />
        <Route path="/" element={<div className="w-full min-h-screen font-sans" data-id="element-13">
              <div className="sticky top-0 z-50" data-id="element-14">
                <AnnouncementBar data-id="element-15" />
                <Navbar data-id="element-16" />
              </div>
              <Hero data-id="element-17" />
              <ImpactMetrics data-id="element-18" />
              <WhyItWorks data-id="element-19" />
              <Features data-id="element-20" />
              <Interfaces data-id="element-21" />
              <Integrations data-id="element-22" />
              <PoweredByFluidWeb data-id="element-23" />
              <AdvancedCapabilities data-id="element-24" />
              <Pricing data-id="element-25" />
              <FAQ data-id="element-26" />
              <CTA data-id="element-27" />
              <Footer data-id="element-28" />
            </div>} data-id="element-12" />
      </Routes>
      <ChatBubble data-id="element-29" />
    </BrowserRouter>;
}